<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    :submitting="submitting"
    :submitTitle="submitTitle"
    :scrollable="true"
    sizeClass="modal-xl"
    icon="fa-tags"
    @closed="onClosed"
    @submitted="onSubmitted">

    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <FormGroup v-bind:form="dialogForm" :labelColumnSize="2" v-if="!loading">
      <TabbedBox ref="tabBox">
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-type-details">
            <i class="far fa-tags"></i> Type Details
          </Tab>
          <Tab tabid="tab-type-settings">
            <i class="far fa-cog"></i> Settings
          </Tab>
          <Tab tabid="tab-type-rules">
            <i class="far fa-ballot-check"></i> Rules
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-type-details">
            <FormRow label="Name">
              <FormInputText v-model="taskTypeData.name" field="name" placeholder="Name" />
            </FormRow>
            <FormRow label="Description">
              <FormInputText v-model="taskTypeData.description" field="description" placeholder="Description" />
            </FormRow>
            <FormRow label="Highlight Colour" helpTip="Highlight colour for the task type used with filtering">
              <FormColourTag v-model="taskTypeData.highlightColour" field="highlightColour" />
            </FormRow>
            <FormRow label="Default Users" helpTip="The users automatically assigned to this type of task by default">
              <FormMultiSelect :options="userList" v-model="defaultUsers" placeholder="Users" field="defaultUsers" />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-type-settings">
            <ContentTitle>Settings</ContentTitle>

            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Lead Time" helpTip="Lead time is used to estimate panel completion dates for orders" :labelColumnSize="6">
                  <FormInputText type="number" v-model="taskTypeData.leadTime" field="leadTime" addon="mins" />
                </FormRow>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Default Task Time" helpTip="Default time to allocate to the task to complete. Ignored for despatch based tasks" :labelColumnSize="6">
                  <FormInputText type="number" v-model="taskTypeData.defaultTaskTime" field="defaultTaskTime" addon="mins" />
                </FormRow>
              </div>
            </div>

            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Expiry Time" helpTip="Time until the task is automatically cancelled if nothing has been scanned in/out and has not started" :labelColumnSize="6">
                  <FormInputText type="number" v-model="taskTypeData.expiryTime" field="expiryTime" addon="mins" note="Set to zero to disable" />
                </FormRow>
              </div>
            </div>

            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Item Handling Time" helpTip="How long to allocate for each item quantity for loading / unloading. Used for route scheduling" :labelColumnSize="6">
                  <FormInputText type="number" v-model="taskTypeData.handlingTimePerItem" field="handlingTimePerItem" addon="mins" />
                </FormRow>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Late Arrival Allowance" helpTip="How long to allow for late arrival for location based tasks. If exceeded, late arrival reason is required on signoff" :labelColumnSize="6">
                  <FormInputText type="number" v-model="taskTypeData.lateArrivalAllowanceTime" field="lateArrivalAllowanceTime" addon="mins" note="Set to zero to disable" />
                </FormRow>
              </div>
            </div>

            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Kiosk - Request Task Enabled" helpTip="If selected, the kiosk will be able to request a task of this type for order despatch" :labelColumnSize="6">
                  <FormCheckbox v-model="taskTypeData.isKioskRequestTaskAvailable" field="isKioskRequestTaskAvailable" />
                </FormRow>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Kiosk - Self Loading Enabled" helpTip="If selected, the kiosk will be able to self load items. Otherwise items will require loading by staff" :labelColumnSize="6">
                  <FormCheckbox v-model="taskTypeData.isKioskLoadItemsAvailable" field="isKioskLoadItemsAvailable" />
                </FormRow>
              </div>
            </div>
            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Kiosk - Complete Task Enabled" helpTip="If selected, the kiosk will be able to complete tasks (as self service) of this type for order despatch" :labelColumnSize="6">
                  <FormCheckbox v-model="taskTypeData.isKioskCompleteTaskAvailable" field="isKioskCompleteTaskAvailable" />
                </FormRow>
              </div>
            </div>

            <div class="row no-gutters">
              <div class="col-6">
                <FormRow label="Dashboard - Display on the main dashboard" helpTip="If selected, the task type will be displayed on the front dashboard" :labelColumnSize="6">
                  <FormCheckbox v-model="taskTypeData.isDashboardDisplayEnabled" field="isDashboardDisplayEnabled" />
                </FormRow>
              </div>
            </div>
          </TabPage>

          <TabPage tabid="tab-type-rules">
            <ContentTitle>Rules</ContentTitle>

            <div class="row no-gutters">
              <div class="col-6">

                <div class="row">
                  <div class="col-12">
                    <FormRow label="Order Item Despatch Enabled" helpTip="If selected, the task type can be linked to despatchable order items" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isOrderItemDespatchEnabled" field="isOrderItemDespatchEnabled" />
                    </FormRow>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <FormRow label="Order Completion Enabled" helpTip="If selected, the task type can be linked to orders with no despatchable order items to complete them. Eg, fee based orders" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isOrderCompleteEnabled" field="isOrderCompleteEnabled" />
                    </FormRow>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <FormRow label="Despatch Enabled" helpTip="If selected, the task type will be available for selection by orders for despatch" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isDespatchEnabled" field="isDespatchEnabled" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Despatch Rule" helpTip="How order items are despatched / invoiced on completion" :labelColumnSize="6">
                      <FormSelect :options="TaskTypeInvoicingRuleTypeLookup" v-model="taskTypeData.invoiceItemRuleType" field="invoiceItemRuleType" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Transfer Enabled" helpTip="If selected, production items and stock can be selected for transfering between locations" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isTransferEnabled" field="isTransferEnabled" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Single Contact Only" helpTip="If selected, the task can only be linked to orders of a single contact" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isSingleContact" field="isSingleContact" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Requires Scan In" helpTip="If selected, the task will require scanning in of panels and stock when loading. If not enabled, items are automatically processed when tasks are completed" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isScanInAvailable" field="isScanInAvailable" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Requires Scan Out" helpTip="If selected, the task will require scanning out of panels and stock when unloading. Requires scan in enabled" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isScanOutAvailable" field="isScanOutAvailable" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Location Enabled" helpTip="If selected, the task will require address details and use location tracking features such as mapping routes and GPS tracking" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isLocationTrackingEnabled" field="isLocationTrackingEnabled" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Primary User Required" helpTip="If selected, the task will require a primary user to be assigned on task creation" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isPrimaryUserRequired" field="isPrimaryUserRequired" />
                    </FormRow>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Loading Notes Required" helpTip="If selected, the task will required notes to be entered when loading is completed" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isLoadingNotesEnabled" field="isLoadingNotesEnabled" />
                    </FormRow>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <FormRow label="Signoff Required" helpTip="If selected, the task items will require a signature or dropoff proof" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isSignoffRequired" field="isSignoffRequired" />
                    </FormRow>
                  </div>
                </div>
              </div>

              <div class="col-6">
                <div class="row">
                  <div class="col-12">
                    <FormRow label="Available Order Service Fees" helpTip="Available order service fees for this task type to add to an order as a despatch fee" :labelColumnSize="6">
                      <FormMultiSelect :options="generalStockList" v-model="availableFeeStocks" placeholder="Service Fees" field="availableFeeStocks" />
                    </FormRow>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <FormRow label="Default Order Service Fee" helpTip="Default fee used if not setup per contact or contact address" :labelColumnSize="6">
                      <FormSelect :options="defaultServiceFeeLookup" v-model="taskTypeData.defaultFeeStockId" placeholder="Default Service Fee" field="defaultFeeStockId" />
                    </FormRow>
                  </div>
                </div>

                <div class="row">
                  <div class="col-12">
                    <FormRow label="No Stock Fee Allowed" helpTip="If selected, a 'None' option will be available on orders to select. Otherwise a stock item must be selected if service fees are in the list above" :labelColumnSize="6">
                      <FormCheckbox v-model="taskTypeData.isNoFeeStockItemAllowed" field="isNoFeeStockItemAllowed" />
                    </FormRow>
                  </div>
                </div>
              </div>
            </div>
          </TabPage>

        </template>
      </TabbedBox>
            
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import { TaskTypeInvoicingRuleTypes, TaskTypeInvoicingRuleTypeLookup, InventoryStockTypes } from '@/store/types'

export default {
  name: 'TaskTypeDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog
  },
  props: {
    isCreateMode: Boolean,
    id: Number,
  },
  data() {
    return {
      dialogForm: {},
      taskTypeData: {
        
      },
      userList: [],
      defaultUsers: [],
      generalStockList: [],
      availableFeeStocks: [],

      submitting: false,
      loading: false,
      loadingError: '',
    }
  },
  watch: {
    availableFeeStocks: function() {
      // If default fee stock is not in available list, clear it
      if(!this.availableFeeStocks.includes(this.taskTypeData.defaultFeeStockId))
        this.taskTypeData.defaultFeeStockId = null
    }
  },
  created() {
    this.TaskTypeInvoicingRuleTypeLookup = TaskTypeInvoicingRuleTypeLookup
  },
  mounted() {
  },
  computed: {
    title() {
      return this.isCreateMode ? 'New Task Type' : 'Edit Task Type'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    defaultServiceFeeLookup() {
      return _.concat({
        value: null,
        name: 'None'
      }, _.compact(this.availableFeeStocks.map(stockId => {
        return this.generalStockList.find(stock => stock.value == stockId)
      })))
    }
  },
  methods: {
    show() {
      // Reset form
      this.taskTypeData = {
        name: '',
        description: '',
        leadTime: 0,
        defaultTaskTime: 30,
        expiryTime: 0,
        handlingTimePerItem: 0,
        lateArrivalAllowanceTime: 0,
        highlightColour: '',
        isOrderItemDespatchEnabled: true,
        isOrderCompleteEnabled: true,
        isDespatchEnabled: false,
        isTransferEnabled: false,
        isScanInAvailable: false,
        isScanOutAvailable: false,
        isLocationTrackingEnabled: false,
        isPrimaryUserRequired: false,
        isLoadingNotesEnabled: false,
        isSingleContact: true,
        isSignoffRequired: false,
        isKioskRequestTaskAvailable: false,
        isKioskLoadItemsAvailable: false,
        isKioskCompleteTaskAvailable: false,
        isDashboardDisplayEnabled: true,
        invoiceItemRuleType: TaskTypeInvoicingRuleTypes.despatchAndInvoice,
        defaultFeeStockId: null,
        defaultUsers: [],
        availableFeeStocks: [],
        isNoFeeStockItemAllowed: false
      }
      this.dialogForm = {}
      this.submitting = false

      this.requestData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.submitting = true

      var updateData = _.cloneDeep(this.$data.taskTypeData)
      
      updateData.defaultUsers = this.defaultUsers
      updateData.availableFeeStocks = this.availableFeeStocks
      
      if(this.isCreateMode) {
        this.$apiService.tasksService.createTaskType(updateData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      } else {
        this.$apiService.tasksService.updateTaskType(updateData.id, updateData)
        .then(response => {
          this.submitting = false
          this.modalClosed(response.data.id)
          this.hide()
        })
        .catch(err => {
          this.submitting = false
          this.$data.dialogForm = this.processFormResponse(err)
        })
      }
    },
    requestData() {
      this.loading = true
      this.loadingError = ''

      var requestList = [
        this.$apiService.userService.getUserList(),
        this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.general }),
      ]
      if(!this.isCreateMode && this.id)
        requestList.push(this.$apiService.tasksService.getTaskType(this.id))

      Promise.all(requestList)
      .then(results => {
        this.userList = results[0].data
        .filter(user => user.isActive && user.isTaskResource)
        .map(user => {
          return {
            value: user.id,
            name: user.name,
            colour: user.highlightColour
          }
        })
        this.generalStockList = results[1].data
        .map(stock => {
          return {
            value: stock.id,
            name: stock.name
          }
        })

        if(!this.isCreateMode && this.id) {
          this.taskTypeData = results[2].data

          this.defaultUsers = this.taskTypeData.defaultUsers.map(user => user.id)
          this.availableFeeStocks = this.taskTypeData.availableFeeStocks.map(stock => stock.id)
        }
      })
      .catch(err => {
        this.loadingError = 'Error loading data. Check your internet connection and try again'
      })
      .finally(() => {
        this.loading = false
      })
    }
  }
}
</script>